/* istanbul ignore file */
// This file contains utility functions for scrolling to elements on the page.
// We don't touch this often, and is difficult to test in isolation,
// so we ignore it in our test coverage.
// If for any reason you need to change this file, please add tests for it.

import smoothscroll from 'smoothscroll-polyfill';
import { TIMEOUT_INTERVAL } from '../constants/Constants';

smoothscroll.polyfill();

interface ICustomScrollOptions {
  scrollOutOfViewportOnly: boolean;
  offsetY: number;
}

type customScrollOptionsType = Partial<ICustomScrollOptions>;

const defaultScrollOptions: customScrollOptionsType = {
  scrollOutOfViewportOnly: false,
};

let scrollInterval: number | null = null;

const DELTA = 1; // Tolerance for scroll precision (e.g., 1 pixel)

const isInViewport = (element: Element): boolean => {
  if (!element) {
    return false;
  }

  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0
    && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
  );
};

export const scrollToTop = (): void => {
  let duration = 500;
  if (scrollInterval) {
    clearInterval(scrollInterval);
  }

  const newInterval = setInterval(() => {
    const scrollTick = ((0 - document.documentElement.scrollTop) / duration) * 10;
    document.documentElement.scrollTop += scrollTick;
    if (document.documentElement.scrollTop === 0) {
      return;
    }
    duration -= 10;
  }, 10);
  scrollInterval = newInterval;

  setTimeout(() => {
    if (newInterval === scrollInterval) {
      clearInterval(scrollInterval);
    }
  }, TIMEOUT_INTERVAL.SIX_HUNDRED);
};

export const scrollToTargets = (
  selectors: string | string[],
  scrollIntoViewOptions = {},
  scrollCustomOptions: customScrollOptionsType = defaultScrollOptions,
): boolean => {
  const selectorArray: string[] = Array.isArray(selectors) ? selectors : [selectors];
  let item: Element | null = null;

  // eslint-disable-next-line no-restricted-syntax
  for (const selector of selectorArray) {
    item = document.querySelector(selector);
    if (item) {
      break;
    }
  }

  const shouldScroll = scrollCustomOptions.scrollOutOfViewportOnly ? !isInViewport(item) : true;

  if (!item || !shouldScroll) {
    return false;
  }

  if (scrollInterval) {
    clearInterval(scrollInterval);
  }

  if (item.scrollIntoView) {
    item.scrollIntoView({
      behavior: 'smooth',
      ...scrollIntoViewOptions,
    });

    return true;
  }

  const { top } = item.getBoundingClientRect();
  const { scrollTop } = document.documentElement;
  const target = top + scrollTop + (scrollCustomOptions.offsetY || 0);

  const newInterval = setInterval(() => {
    const current = document.documentElement.scrollTop;
    const step = (target - current) / 10;

    if (Math.abs(target - current) < DELTA) {
      document.documentElement.scrollTop = target;
      clearInterval(scrollInterval);
      scrollInterval = null;
      return;
    }

    document.documentElement.scrollTop += step;
  }, 10);
  scrollInterval = newInterval;

  setTimeout(() => {
    if (scrollInterval === newInterval) {
      clearInterval(scrollInterval);
    }
  }, TIMEOUT_INTERVAL.SIX_HUNDRED);

  return true;
};
